import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send } from "lucide-react";

export default function AdminTickets() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const load = () => api.get("/admin/tickets").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!selected || !reply.trim()) return;
    try {
      await api.post(`/admin/tickets/${selected.id}/reply`, { message: reply });
      setReply("");
      await load();
      const fresh = (await api.get("/admin/tickets")).data.find(t => t.id === selected.id);
      setSelected(fresh || null);
      toast.success("Reply sent");
    } catch { toast.error("Failed"); }
  };

  const setStatus = async (id, status) => {
    await api.patch(`/admin/tickets/${id}/status`, { status });
    await load();
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  return (
    <>
      <h1 className="text-2xl font-semibold tracking-tight">Support Tickets</h1>
      <p className="text-sm text-muted-foreground mt-1">Customer support conversations.</p>

      <div className="mt-6 grid gap-4 md:grid-cols-[380px_1fr]">
        <div className="card-elevated divide-y divide-black/5 dark:divide-white/10 max-h-[75vh] overflow-y-auto" data-testid="admin-tickets-list">
          {rows.map(t => (
            <button key={t.id} onClick={() => setSelected(t)} data-testid={`admin-ticket-item-${t.id}`}
              className={`w-full text-left p-4 hover:bg-black/[0.02] dark:hover:bg-white/[0.02] ${selected?.id === t.id ? "bg-black/[0.03] dark:bg-white/[0.03]" : ""}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium tracking-tight truncate">{t.subject}</div>
                <span className="text-xs rounded-full bg-[#1565FF]/10 text-[#1565FF] px-2 py-0.5">{t.status}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">{t.customer_email} · {t.priority}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">Updated {new Date(t.updated_at).toLocaleString()}</div>
            </button>
          ))}
          {rows.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No tickets yet.</p>}
        </div>

        <div className="card-elevated p-6">
          {!selected ? (
            <p className="text-muted-foreground text-sm">Select a ticket to view.</p>
          ) : (
            <>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight">{selected.subject}</h2>
                  <p className="text-xs text-muted-foreground">{selected.customer_name} · {selected.customer_email}</p>
                </div>
                <select value={selected.status} onChange={(e) => setStatus(selected.id, e.target.value)} data-testid="admin-ticket-status"
                  className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-1.5 text-xs">
                  <option value="open">open</option><option value="answered">answered</option><option value="closed">closed</option>
                </select>
              </div>
              <div className="mt-4 space-y-3 max-h-[55vh] overflow-y-auto pr-2">
                {(selected.messages || []).map((m, i) => (
                  <div key={i} className={`flex ${m.author === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${m.author === "admin" ? "bg-[#1565FF] text-white" : "bg-black/5 dark:bg-white/5"}`}>
                      <div className="text-[10px] opacity-70 mb-1">{m.name || m.author} · {new Date(m.at).toLocaleString()}</div>
                      <div className="whitespace-pre-wrap">{m.message}</div>
                    </div>
                  </div>
                ))}
              </div>
              {selected.status !== "closed" && (
                <div className="mt-4 pt-4 border-t border-black/5 dark:border-white/10 flex gap-2">
                  <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply to customer…" data-testid="admin-ticket-reply-input"
                    className="flex-1 rounded-full border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-2 text-sm" />
                  <button onClick={send} className="btn-primary !py-2 !px-4 !text-sm" data-testid="admin-ticket-reply-send"><Send className="h-3.5 w-3.5"/> Send</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
